import { exceptions } from "@decentriq/utils";
import { Button } from "@mui/joy";
import { useBoolean } from "ahooks";
import { type SnackbarKey } from "notistack";
import {
  type Dispatch,
  memo,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { MediaDataRoomVersionSwitch } from "features/mediaDataRoom/components";
import {
  useAdvertiserAudiences,
  useMediaDataRoom,
} from "features/mediaDataRoom/contexts";
import {
  type LookalikeAudienceStatistics,
  type MediaDataRoomJobHookResultWithoutCacheSetter,
  mergeMediaDataRoomJobResults,
} from "features/mediaDataRoom/hooks";
import { useLookalikeAudienceStatistics as useLookalikeAudienceAbStatistics } from "features/mediaDataRoom/hooks/ab";
import { useLookalikeAudienceStatistics as useLookalikeAudienceV3Statistics } from "features/mediaDataRoom/hooks/v3";
import {
  type LookalikeAudience,
  MediaDataRoomTab,
  MediaDataRoomVersion,
} from "features/mediaDataRoom/models";
import {
  mapMediaDataRoomErrorToSnackbar,
  useDataRoomSnackbar,
  useOrganizationPreferences,
} from "hooks";
import { dataRoomPathBuilder, DataRoomTypeNames } from "models";
import {
  AdvertiserAudienceGeneratorEmptyState,
  AdvertiserAudienceGeneratorErrorState,
  AdvertiserAudienceGeneratorLoadingState,
  AdvertiserAudienceGeneratorStep,
  useAdvertiserAudienceGenerator,
} from "../../AdvertiserAudienceGenerator";
import {
  LookalikeAdvertiserAudienceGeneratorConfigurationStep,
  LookalikeAdvertiserAudienceGeneratorSummaryStep,
} from "../components";
import {
  type LookalikeAdvertiserAudienceGeneratorContextValue,
  LookalikeAdvertiserAudienceGeneratorProvider,
} from "../contexts/LookalikeAdvertiserAudienceGeneratorContext";
import {
  type LookalikeAudienceSeedOption,
  mapAudienceKindToSeedOptionGroup,
  seedOptionGroupPriority,
} from "../models";

const DEFAULT_REACH_VALUE: number = 5;

interface LookalikeAdvertiserAudienceGeneratorProps {
  selectedAudienceStatistics: LookalikeAudienceStatistics;
  selectedSeedOption: LookalikeAudienceSeedOption | null;
  setSelectedSeedOption: Dispatch<
    SetStateAction<LookalikeAudienceSeedOption | null>
  >;
  seedOptions: MediaDataRoomJobHookResultWithoutCacheSetter<
    LookalikeAudienceSeedOption[]
  >;
  excludeSeedAudience: boolean;
  setExcludeSeedAudience: Dispatch<SetStateAction<boolean>>;
  reach: number;
  setReach: Dispatch<SetStateAction<number>>;
  saveAudience: (audience: LookalikeAudience) => Promise<void>;
  showExcludeSeedAudienceCheckbox: boolean;
  disableExcludeSeedAudienceCheckbox: boolean;
}

const LookalikeAdvertiserAudienceGenerator =
  memo<LookalikeAdvertiserAudienceGeneratorProps>(
    ({
      seedOptions,
      saveAudience,
      selectedAudienceStatistics,
      selectedSeedOption,
      setSelectedSeedOption,
      excludeSeedAudience,
      setExcludeSeedAudience,
      reach,
      setReach,
      showExcludeSeedAudienceCheckbox,
      disableExcludeSeedAudienceCheckbox,
    }) => {
      const { dataRoomId, isDeactivated, version } = useMediaDataRoom();
      const { currentStep } = useAdvertiserAudienceGenerator();
      const [audienceName, setAudienceName] = useState<string>("");
      const navigate = useNavigate();
      const { enqueueSnackbar } = useDataRoomSnackbar();
      // Advertiser audience Quality drawer state
      const [
        isAdvertiserAudienceQualityDrawerOpen,
        {
          setTrue: openAdvertiserAudienceQualityDrawer,
          setFalse: closeAdvertiserAudienceQualityDrawer,
        },
      ] = useBoolean(false);
      const [generationStep, setGenerationStep] =
        useState<
          LookalikeAdvertiserAudienceGeneratorContextValue["generationStep"]
        >(null);
      // Set default value for the new audience
      useEffect(() => {
        setAudienceName(`${selectedSeedOption?.name} - ${reach}%`);
      }, [reach, currentStep, selectedSeedOption?.name]);
      // Set default value for audienceType selector
      useEffect(() => {
        const nonEmptySeedOptions = seedOptions?.computeResults?.filter(
          ({ isEmpty }) => !isEmpty
        );
        if (nonEmptySeedOptions?.length && !selectedSeedOption) {
          setSelectedSeedOption(nonEmptySeedOptions[0]);
        }
      }, [seedOptions, selectedSeedOption, setSelectedSeedOption]);
      const generateAudience = useCallback(async () => {
        try {
          if (!selectedSeedOption?.id) {
            enqueueSnackbar("Please select seed audience", {
              variant: "info",
            });
            return;
          }
          if (!audienceName.trim()) {
            enqueueSnackbar("Please provide a name for the audience", {
              variant: "info",
            });
            return;
          }
          setGenerationStep("pending");
          await saveAudience({
            createdAt: new Date().toISOString(),
            excludeSeedAudience,
            id: crypto.randomUUID(),
            kind: "lookalike",
            name: audienceName,
            reach,
            sourceRef: selectedSeedOption.id,
            status: "ready",
          });
          setTimeout(() => {
            const dataRoomBasePath = dataRoomPathBuilder(
              dataRoomId,
              DataRoomTypeNames.PublishedMediaInsightsDcr
            );
            navigate(`${dataRoomBasePath}/${MediaDataRoomTab.activation}`);
            enqueueSnackbar("Audience successfully created.", {
              variant: "success",
            });
          }, 1500);
        } catch (error) {
          setGenerationStep(null);
          enqueueSnackbar(
            ...mapMediaDataRoomErrorToSnackbar(
              error,
              "Failed to generate audience"
            )
          );
        }
      }, [
        setGenerationStep,
        audienceName,
        selectedSeedOption?.id,
        saveAudience,
        excludeSeedAudience,
        reach,
        dataRoomId,
        navigate,
        enqueueSnackbar,
      ]);
      const [retryInProgress, setRetryInProgress] = useState(false);
      const retryGeneration = useCallback(async () => {
        if (isDeactivated) return;
        setRetryInProgress(true);
        try {
          await seedOptions.retry();
        } finally {
          setRetryInProgress(false);
        }
      }, [seedOptions, isDeactivated]);
      if (seedOptions.error) {
        return (
          <AdvertiserAudienceGeneratorErrorState
            RetryButton={
              !isDeactivated && (
                <Button
                  color="neutral"
                  loading={retryInProgress}
                  onClick={retryGeneration}
                  variant="soft"
                >
                  Retry
                </Button>
              )
            }
          />
        );
      }
      if (seedOptions.loading) {
        return <AdvertiserAudienceGeneratorLoadingState />;
      }
      if (!seedOptions?.computeResults?.length) {
        return <AdvertiserAudienceGeneratorEmptyState />;
      }
      let step: ReactNode = null;
      switch (currentStep) {
        case AdvertiserAudienceGeneratorStep.CONFIGURATION:
          step = <LookalikeAdvertiserAudienceGeneratorConfigurationStep />;
          break;
        case AdvertiserAudienceGeneratorStep.SUMMARY:
          step = <LookalikeAdvertiserAudienceGeneratorSummaryStep />;
          break;
      }
      return (
        <LookalikeAdvertiserAudienceGeneratorProvider
          value={{
            audienceName,
            closeAdvertiserAudienceQualityDrawer,
            disableExcludeSeedAudienceCheckbox,
            excludeSeedAudience,
            generateAudience,
            generationStep,
            isAdvertiserAudienceQualityDrawerOpen,
            openAdvertiserAudienceQualityDrawer,
            reach,
            seedOptions,
            selectedAudienceStatistics,
            selectedSeedOption: selectedSeedOption,
            setAudienceName,
            setExcludeSeedAudience,
            setReach,
            setSelectedSeedOption: setSelectedSeedOption,
            showExcludeSeedAudienceCheckbox,
            version,
          }}
        >
          {step}
        </LookalikeAdvertiserAudienceGeneratorProvider>
      );
    }
  );
LookalikeAdvertiserAudienceGenerator.displayName =
  "LookalikeAdvertiserAudienceGenerator";

const LookalikeAdvertiserAudienceAbGenerator = memo(() => {
  const { enqueueSnackbar, closeSnackbar } = useDataRoomSnackbar();
  const setErrorSnackbarId = useState<SnackbarKey | undefined>(undefined)[1];
  const [selectedSeedOption, setSelectedSeedOption] =
    useState<LookalikeAudienceSeedOption | null>(null);
  const { allowExcludingSeedAudience: showExcludeSeedAudienceCheckbox } =
    useOrganizationPreferences();
  const [excludeSeedAudience, setExcludeSeedAudience] =
    useState<boolean>(false);
  const [reach, setReach] = useState<number>(DEFAULT_REACH_VALUE);
  const {
    saveAudience,
    audiences,
    getParameterPayloads,
    doesAudienceDependOnLookalikeAudience,
  } = useAdvertiserAudiences(MediaDataRoomVersion.ab);
  const selectedAudienceStatistics: LookalikeAudienceStatistics =
    useLookalikeAudienceAbStatistics({
      audienceId: selectedSeedOption?.id,
      audienceReach: reach,
      excludeSeedAudience,
      getParameterPayloads,
    });
  useEffect(
    () =>
      setErrorSnackbarId((currentSnackbarId) => {
        if (selectedAudienceStatistics.computeState.error) {
          const snackbarId = enqueueSnackbar(
            ...mapMediaDataRoomErrorToSnackbar(
              selectedAudienceStatistics.computeState.error,
              "Unable to fetch lookalike audience quality statistics"
            )
          );
          return snackbarId;
        }
        if (currentSnackbarId) {
          closeSnackbar(currentSnackbarId);
        }
        return undefined;
      }),
    [
      selectedAudienceStatistics.computeState.error,
      enqueueSnackbar,
      closeSnackbar,
      setErrorSnackbarId,
    ]
  );
  const seedOptions = useMemo<
    MediaDataRoomJobHookResultWithoutCacheSetter<LookalikeAudienceSeedOption[]>
  >(
    () =>
      mergeMediaDataRoomJobResults([audiences], ([audiences]) =>
        audiences
          .filter(({ id }) => !doesAudienceDependOnLookalikeAudience(id))
          .map(({ id, name, kind }) => ({
            group: mapAudienceKindToSeedOptionGroup[kind],
            id,
            name,
          }))
          .sort(
            ({ group: aGroup }, { group: bGroup }) =>
              seedOptionGroupPriority[aGroup] - seedOptionGroupPriority[bGroup]
          )
      ),
    [audiences, doesAudienceDependOnLookalikeAudience]
  );
  const lookalikeAudiences = useMemo<LookalikeAudience[] | undefined>(
    () =>
      audiences.computeResults?.filter(
        (audience) => audience.kind === "lookalike"
      ),
    [audiences.computeResults]
  );
  useEffect(() => {
    if (!lookalikeAudiences) return;
    const isExcludeSeedAudienceCreated = lookalikeAudiences.some(
      (audience) => audience.excludeSeedAudience
    );
    if (!showExcludeSeedAudienceCheckbox || !isExcludeSeedAudienceCreated)
      return;
    setExcludeSeedAudience(isExcludeSeedAudienceCreated);
  }, [
    showExcludeSeedAudienceCheckbox,
    setExcludeSeedAudience,
    lookalikeAudiences,
  ]);
  return (
    <LookalikeAdvertiserAudienceGenerator
      disableExcludeSeedAudienceCheckbox={Boolean(lookalikeAudiences?.length)}
      excludeSeedAudience={excludeSeedAudience}
      reach={reach}
      saveAudience={saveAudience}
      seedOptions={seedOptions}
      selectedAudienceStatistics={selectedAudienceStatistics}
      selectedSeedOption={selectedSeedOption}
      setExcludeSeedAudience={setExcludeSeedAudience}
      setReach={setReach}
      setSelectedSeedOption={setSelectedSeedOption}
      showExcludeSeedAudienceCheckbox={showExcludeSeedAudienceCheckbox}
    />
  );
});
LookalikeAdvertiserAudienceAbGenerator.displayName =
  "LookalikeAdvertiserAudienceAbGenerator";

const LookalikeAdvertiserAudienceV3Generator = memo(() => {
  const { enqueueSnackbar, closeSnackbar } = useDataRoomSnackbar();
  const setErrorSnackbarId = useState<SnackbarKey | undefined>(undefined)[1];
  const { allowExcludingSeedAudience: showExcludeSeedAudienceCheckbox } =
    useOrganizationPreferences();
  const [selectedSeedAudience, setSelectedSeedAudience] =
    useState<LookalikeAudienceSeedOption | null>(null);
  const [excludeSeedAudience, setExcludeSeedAudience] =
    useState<boolean>(false);
  const [reach, setReach] = useState<number>(DEFAULT_REACH_VALUE);
  const {
    saveAudience,
    audiences,
    seedAudiences,
    audienceSizes: { audienceSizes },
  } = useAdvertiserAudiences(MediaDataRoomVersion.v3);
  const selectedAudienceStatistics: LookalikeAudienceStatistics =
    useLookalikeAudienceV3Statistics({
      audienceId: selectedSeedAudience?.id,
      audienceReach: reach,
      audiences: audiences?.computeResults,
      excludeSeedAudience,
      seedAudiences: seedAudiences?.computeResults,
      skip: selectedSeedAudience?.isEmpty ?? false,
    });
  useEffect(
    () =>
      setErrorSnackbarId((currentSnackbarId) => {
        if (
          selectedAudienceStatistics.computeState.error &&
          !(
            selectedAudienceStatistics.computeState.error instanceof
            exceptions.MediaDataRoomNoScoredUsersError
          )
        ) {
          const snackbarId = enqueueSnackbar(
            ...mapMediaDataRoomErrorToSnackbar(
              selectedAudienceStatistics.computeState.error,
              "Unable to fetch lookalike audience quality statistics"
            )
          );
          return snackbarId;
        }
        if (currentSnackbarId) {
          closeSnackbar(currentSnackbarId);
        }
        return undefined;
      }),
    [
      selectedAudienceStatistics.computeState.error,
      enqueueSnackbar,
      closeSnackbar,
      setErrorSnackbarId,
    ]
  );
  const seedOptions = useMemo<
    MediaDataRoomJobHookResultWithoutCacheSetter<LookalikeAudienceSeedOption[]>
  >(
    () =>
      mergeMediaDataRoomJobResults(
        [audiences, seedAudiences],
        ([audiences, seedAudiences]) =>
          [
            ...audiences
              .filter(
                ({
                  doesDependOnLookalikeAudience,
                  isStale,
                  canBeViewedByCurrentUser,
                }) =>
                  !doesDependOnLookalikeAudience &&
                  !isStale &&
                  canBeViewedByCurrentUser
              )
              .map(({ id, name, kind }) => ({
                group: mapAudienceKindToSeedOptionGroup[kind],
                id,
                isEmpty: audienceSizes[id]?.audienceSize === 0,
                name,
              })),
            ...seedAudiences.map(({ id, name, isEmpty }) => ({
              group: "Seed" as const,
              id,
              isEmpty,
              name,
            })),
          ].sort(
            ({ group: aGroup }, { group: bGroup }) =>
              seedOptionGroupPriority[aGroup] - seedOptionGroupPriority[bGroup]
          )
      ),
    [audiences, seedAudiences, audienceSizes]
  );
  return (
    <LookalikeAdvertiserAudienceGenerator
      disableExcludeSeedAudienceCheckbox={false}
      excludeSeedAudience={excludeSeedAudience}
      reach={reach}
      saveAudience={saveAudience}
      seedOptions={seedOptions}
      selectedAudienceStatistics={selectedAudienceStatistics}
      selectedSeedOption={selectedSeedAudience}
      setExcludeSeedAudience={setExcludeSeedAudience}
      setReach={setReach}
      setSelectedSeedOption={setSelectedSeedAudience}
      showExcludeSeedAudienceCheckbox={showExcludeSeedAudienceCheckbox}
    />
  );
});
LookalikeAdvertiserAudienceV3Generator.displayName =
  "LookalikeAdvertiserAudienceV3Generator";

const LookalikeAdvertiserAudienceGeneratorVersioned = memo(() => (
  <MediaDataRoomVersionSwitch
    ab={<LookalikeAdvertiserAudienceAbGenerator />}
    v3={<LookalikeAdvertiserAudienceV3Generator />}
  />
));
LookalikeAdvertiserAudienceGeneratorVersioned.displayName =
  "LookalikeAdvertiserAudienceGeneratorVersioned";

export default LookalikeAdvertiserAudienceGeneratorVersioned;
